/**
 * Loader — pull scripts and stylesheets in at runtime.
 *
 * Sequential: each entry waits for the previous, so dependency order holds. Idempotent per URL
 * (ignoring any ?ver cache-buster), so calling twice costs one request.
 *
 * Hoisted from app/connect-bar/helpers.js (where it was scripts-only) — nothing about it is
 * connect-bar-specific, and stylesheets need the same treatment.
 */
const Loader = {};

/** url → promise, so a second request for the same asset reuses the first. */
const loaded = new Map();

const keyOf = (url) => String(url).split("?")[0];

function inject(url, build) {
  const key = keyOf(url);

  if (loaded.has(key)) return loaded.get(key);

  const promise = new Promise((resolve, reject) => {
    const el = build();

    el.onload = resolve;
    el.onerror = reject;

    document.head.appendChild(el);
  });

  loaded.set(key, promise);

  return promise;
}

/** Run entries one after another, resolving when all have loaded. */
const sequential = (entries, load) =>
  (entries || []).reduce((chain, entry) => chain.then(() => load(entry)), Promise.resolve());

/**
 * @param  {Array<string|{src:string,module?:boolean,crossorigin?:string}>} entries
 * @return {Promise<void>}
 */
Loader.loadScripts = function (entries) {
  return sequential(entries, (entry) => {
    const { src, module, crossorigin } = typeof entry === "string" ? { src: entry } : entry;

    return inject(src, () => {
      const el = document.createElement("script");

      el.src = src;
      if (module) el.type = "module";
      if (crossorigin) el.crossOrigin = crossorigin;

      return el;
    });
  });
};

/**
 * @param  {Array<string|{href:string,media?:string,crossorigin?:string}>} entries
 * @return {Promise<void>}
 */
Loader.loadStyles = function (entries) {
  return sequential(entries, (entry) => {
    const { href, media, crossorigin } = typeof entry === "string" ? { href: entry } : entry;

    return inject(href, () => {
      const el = document.createElement("link");

      el.rel = "stylesheet";
      el.href = href;
      if (media) el.media = media;
      if (crossorigin) el.crossOrigin = crossorigin;

      return el;
    });
  });
};

export default Loader;
